import React, { useCallback, useEffect, useState } from "react";
import { Box, Button, IconButton, Paper, Slide, Typography, useTheme } from "@mui/material";
import { X } from "react-feather";
import { useTranslation } from "react-i18next";
import { INSTALL_PROMPT_EVENT, getDeferredInstallPrompt } from "src/_utils/installPrompt";

/*
 * "Install app" banner, shown on PHONES AND TABLETS ONLY (see isMobileDevice), for
 * the two platforms that need completely different handling.
 *
 * ANDROID / DESKTOP CHROME
 *   Chrome fires `beforeinstallprompt` when the app qualifies. Its own automatic
 *   banner ("mini-infobar") is heavily throttled — shown at most once, suppressed
 *   for ~90 days after a dismissal, and never when the app is already installed —
 *   so it cannot be relied on. We capture the event in src/_utils/installPrompt.js
 *   (registered before React mounts, because it can fire that early), then show our
 *   own button that calls prompt() on demand.
 *
 * iOS
 *   Safari never fires `beforeinstallprompt` and offers no install UI whatsoever.
 *   The only route is Share -> "Add to Home Screen", which users do not discover
 *   unaided, so we show instructions instead of a button. Note that Chrome/Firefox/
 *   Edge and in-app browsers on iOS cannot install at all — they get told to reopen
 *   the page in Safari.
 */

const STORAGE_KEY = "mapit_install_hint_dismissed_at";
const DISMISS_DAYS = 14;
const SHOW_DELAY_MS = 3000; // let the page settle before interrupting

// iPadOS 13+ reports itself as a Mac, so fall back to touch-point sniffing.
const isIPadOS = () =>
  window.navigator.platform === "MacIntel" && window.navigator.maxTouchPoints > 1;

const isIosDevice = () => /iPad|iPhone|iPod/.test(window.navigator.userAgent || "") || isIPadOS();

/*
 * Phones and tablets only. Desktop Chrome and Edge do fire `beforeinstallprompt`,
 * but a bottom banner stretched across a monitor is noise for merchants and admins
 * working in the dashboard — and installing matters far less there. Desktop users
 * can still install from Chrome's address-bar icon or the ⋮ menu.
 */
const isMobileDevice = () => {
  const ua = window.navigator.userAgent || "";
  return (
    /Android|iPhone|iPad|iPod|Windows Phone|webOS|BlackBerry|Opera Mini|IEMobile/i.test(ua) ||
    isIPadOS()
  );
};

const isStandalone = () =>
  window.navigator.standalone === true ||
  (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches);

// Every iOS browser embeds Safari, but only real Safari exposes Add to Home Screen.
const isRealSafari = () => {
  const ua = window.navigator.userAgent || "";
  return !/CriOS|FxiOS|EdgiOS|OPiOS|GSA|Line|FBAN|FBAV|Instagram/i.test(ua);
};

const wasRecentlyDismissed = () => {
  try {
    const at = window.localStorage.getItem(STORAGE_KEY);
    if (!at) return false;
    const days = (Date.now() - Number(at)) / (1000 * 60 * 60 * 24);
    return Number.isFinite(days) && days < DISMISS_DAYS;
  } catch (error) {
    return false; // private mode / storage blocked — treat as not dismissed
  }
};

// The iOS Share glyph (square with an arrow out of the top) so the instructions
// point at something the user can actually recognise in the toolbar.
const ShareIcon = ({ color }) => (
  <Box
    component="svg"
    viewBox="0 0 50 50"
    aria-hidden="true"
    sx={{ width: 20, height: 20, flexShrink: 0, mx: 0.5, verticalAlign: "middle" }}
  >
    <path
      fill={color}
      d="M25 3a1 1 0 0 0-.7.3l-7 7a1 1 0 1 0 1.4 1.4L24 6.4V32a1 1 0 1 0 2 0V6.4l5.3 5.3a1 1 0 1 0 1.4-1.4l-7-7A1 1 0 0 0 25 3zM13 16a3 3 0 0 0-3 3v25a3 3 0 0 0 3 3h24a3 3 0 0 0 3-3V19a3 3 0 0 0-3-3h-6a1 1 0 1 0 0 2h6a1 1 0 0 1 1 1v25a1 1 0 0 1-1 1H13a1 1 0 0 1-1-1V19a1 1 0 0 1 1-1h6a1 1 0 1 0 0-2h-6z"
    />
  </Box>
);

const InstallAppPrompt = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState(null); // "prompt" | "ios-safari" | "ios-other"

  const dismiss = useCallback((remember) => {
    setOpen(false);
    if (!remember) return;
    try {
      window.localStorage.setItem(STORAGE_KEY, String(Date.now()));
    } catch (error) {
      // Storage unavailable — the hint simply reappears next visit.
    }
  }, []);

  useEffect(() => {
    if (!isMobileDevice() || isStandalone() || wasRecentlyDismissed()) return undefined;

    let timer = null;
    const show = (nextMode) => {
      setMode(nextMode);
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => setOpen(true), SHOW_DELAY_MS);
    };

    if (isIosDevice()) {
      show(isRealSafari() ? "ios-safari" : "ios-other");
    } else if (getDeferredInstallPrompt()) {
      // Chrome already decided the app is installable before React mounted.
      show("prompt");
    }

    // ...or it decides later, once its engagement heuristics are satisfied.
    const onInstallable = () => {
      if (!isIosDevice()) show("prompt");
    };
    // Hide immediately if the user installs by any route (menu, our button, etc).
    const onInstalled = () => dismiss(true);

    window.addEventListener(INSTALL_PROMPT_EVENT, onInstallable);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      if (timer) clearTimeout(timer);
      window.removeEventListener(INSTALL_PROMPT_EVENT, onInstallable);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, [dismiss]);

  const handleInstall = async () => {
    const deferred = getDeferredInstallPrompt();
    if (!deferred) return;
    deferred.prompt();
    try {
      await deferred.userChoice;
    } catch (error) {
      // User dismissed the native sheet — nothing more to do.
    }
    // The event is single-use; Chrome fires a fresh one if still installable.
    dismiss(true);
  };

  return (
    <Slide direction="up" in={open} mountOnEnter unmountOnExit>
      <Paper
        elevation={8}
        role="dialog"
        aria-live="polite"
        sx={{
          position: "fixed",
          zIndex: (t2) => t2.zIndex.snackbar,
          left: 8,
          right: 8,
          // clear the iOS home indicator when the device reports a safe area
          bottom: "calc(8px + env(safe-area-inset-bottom, 0px))",
          p: 1.5,
          borderRadius: 2,
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <Box
          component="img"
          src="/apple-touch-icon.png"
          alt=""
          sx={{ width: 40, height: 40, borderRadius: 1.5, flexShrink: 0 }}
        />

        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.25 }}>
            {t("installApp.title")}
          </Typography>

          {mode === "ios-safari" && (
            <Typography variant="body2" color="text.secondary">
              {t("installApp.iosStep1")}
              <ShareIcon color={theme.palette.primary.main} />
              {t("installApp.iosStep2")}
            </Typography>
          )}

          {mode === "ios-other" && (
            <Typography variant="body2" color="text.secondary">
              {t("installApp.openInSafari")}
            </Typography>
          )}

          {mode === "prompt" && (
            <Typography variant="body2" color="text.secondary">
              {t("installApp.body")}
            </Typography>
          )}
        </Box>

        {mode === "prompt" && (
          <Button variant="contained" size="small" onClick={handleInstall} sx={{ flexShrink: 0 }}>
            {t("installApp.action")}
          </Button>
        )}

        <IconButton
          size="small"
          onClick={() => dismiss(true)}
          aria-label={t("Close")}
          sx={{ flexShrink: 0 }}
        >
          <X size={18} />
        </IconButton>
      </Paper>
    </Slide>
  );
};

export default InstallAppPrompt;
